const ONGOING_STATUSES = ['GUIDED_FLOW', 'AI_ACTIVE', 'HUMAN_ACTIVE'];

// My Tickets is scoped by assignee, but abandoned tickets are the Abandoned
// tab's job — every status except ABANDONED.
const MY_TICKETS_STATUSES = [...ONGOING_STATUSES, 'ESCALATED', 'RESOLVED'];

/**
 * Statuses each tab is allowed to show. `null` = no status constraint.
 * Drives both the query builder and the sidebar's status multi-select options.
 */
export const TAB_ALLOWED_STATUSES = {
  'my-tickets': MY_TICKETS_STATUSES,
  'escalated-tickets': ['ESCALATED'],
  'ongoing-tickets': ONGOING_STATUSES,
  'resolved-tickets': ['RESOLVED'],
  'abandoned-tickets': ['ABANDONED'],
  // Anonymous tab spans every status; it's scoped to the ANONYMOUS origin
  // server-side via the `origins` filter (see buildTabListInput), not by status.
  'anonymous-tickets': null,
  // Scoped server-side by assignedToMe + awaitingReply, whatever the status.
  'awaiting-reply': null,
};

/** Sidebar fields only; tab drives status / assignedAgentId; header drives search. */
export const EMPTY_SIDEBAR_FILTERS = {
  categoryIds: [],
  featureIds: [],
  language: undefined,
  featureIssueId: undefined,
  status: undefined,
  statuses: [],
  unrated: false,
  staleAfter: null,
  aiTurnCountMin: undefined,
  csatScoreMin: undefined,
  csatScoreMax: undefined,
};

export const hasActiveSidebarFilters = filters =>
  Object.keys(EMPTY_SIDEBAR_FILTERS).some(key => {
    const value = filters?.[key];
    if (Array.isArray(value)) return value.length > 0;
    return (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      value !== false &&
      value !== 'ALL'
    );
  });

export function unwrapAdminTickets(data) {
  const node = data?.getAdminTickets;
  if (!node) return {error: null, rows: [], pagination: null};
  if (node.__typename === 'Error') {
    return {error: node, rows: [], pagination: null};
  }
  if (node.__typename === 'ResponseWithHelpdeskTickets') {
    return {error: null, rows: node.data || [], pagination: node.pagination};
  }
  return {error: null, rows: [], pagination: null};
}

export function buildTabListInput(
  activeTab,
  adminId,
  search,
  sidebar,
  pagination,
) {
  const input = {product: 'MOBILE'};
  const s = (search || '').trim();
  if (s) input.search = s;
  // Topic = the help-flow category / feature behind the issue the customer
  // picked. (The old fixed `intent` codes never matched any ticket.)
  if (sidebar?.categoryIds?.length) input.categoryIds = sidebar.categoryIds;
  if (sidebar?.featureIds?.length) input.featureIds = sidebar.featureIds;
  if (sidebar?.language && sidebar.language !== 'ALL') {
    input.language = sidebar.language;
  }
  const featureIssueId = (sidebar?.featureIssueId || '').trim();
  if (featureIssueId) input.featureIssueId = featureIssueId;
  if (Array.isArray(sidebar?.userIds) && sidebar.userIds.length) {
    input.userIds = sidebar.userIds;
  }
  if (sidebar?.unrated) input.unrated = true;
  if (sidebar?.staleAfter) input.staleAfter = sidebar.staleAfter;
  if (typeof sidebar?.aiTurnCountMin === 'number') {
    input.aiTurnCountMin = sidebar.aiTurnCountMin;
  }
  // CSAT score range. Backend enforces csatScoreMin ≤ csatScoreMax; mirror here
  // so a bad client-side combo doesn't even fire the round-trip.
  const csMin =
    typeof sidebar?.csatScoreMin === 'number' ? sidebar.csatScoreMin : null;
  const csMax =
    typeof sidebar?.csatScoreMax === 'number' ? sidebar.csatScoreMax : null;
  if (csMin !== null && csMax !== null && csMin > csMax) {
    // skip — invalid range
  } else {
    if (csMin !== null) input.csatScoreMin = csMin;
    if (csMax !== null) input.csatScoreMax = csMax;
  }
  input.pagination = {
    paginate: true,
    page: pagination?.page || 1,
    limit: pagination?.limit || 12,
    offset:
      typeof pagination?.offset === 'number'
        ? pagination.offset
        : ((pagination?.page || 1) - 1) * (pagination?.limit || 12),
  };

  // Each tab constrains the allowed status set (null = no constraint).
  if (activeTab === 'my-tickets' && adminId) {
    input.assignedAgentId = adminId;
  }
  // No sort → the server returns these oldest-waiting first.
  if (activeTab === 'awaiting-reply') {
    input.assignedToMe = true;
    input.awaitingReply = true;
  }
  // Anonymous tab is scoped server-side via the `origins` filter on
  // AdminTicketFilterInput (a list of TicketOrigin). This lets the server
  // paginate correctly over just anonymous tickets, instead of returning a
  // page of mixed origins that we then narrow client-side.
  if (activeTab === 'anonymous-tickets') {
    input.origins = ['ANONYMOUS'];
  }
  const tabStatuses = TAB_ALLOWED_STATUSES[activeTab] || null;

  // Sidebar status multi-select narrows within the tab's allowed set.
  const sidebarStatuses = [
    ...(Array.isArray(sidebar?.statuses) ? sidebar.statuses : []),
    ...(sidebar?.status ? [sidebar.status] : []), // legacy singular
  ].filter(Boolean);

  let finalStatuses;
  if (tabStatuses && sidebarStatuses.length) {
    const allowed = new Set(tabStatuses);
    const intersect = sidebarStatuses.filter(st => allowed.has(st));
    finalStatuses = intersect.length ? intersect : tabStatuses;
  } else if (tabStatuses) {
    finalStatuses = tabStatuses;
  } else if (sidebarStatuses.length) {
    finalStatuses = sidebarStatuses;
  }

  if (finalStatuses && finalStatuses.length) {
    if (finalStatuses.length === 1) {
      input.status = finalStatuses[0];
    } else {
      input.statuses = finalStatuses;
    }
  }

  return input;
}

/** GraphQL input for getAdminTickets (only fields on AdminTicketFilterInput). */
export function buildAdminTicketsListInput(
  activeTab,
  adminId,
  search,
  applied,
  pagination,
) {
  return buildTabListInput(
    activeTab,
    adminId,
    search,
    {
      categoryIds: applied?.categoryIds,
      featureIds: applied?.featureIds,
      language: applied?.language,
      featureIssueId: applied?.featureIssueId,
      userIds: applied?.userIds,
      status: applied?.status,
      statuses: applied?.statuses,
      unrated: applied?.unrated,
      staleAfter: applied?.staleAfter,
      aiTurnCountMin: applied?.aiTurnCountMin,
      csatScoreMin: applied?.csatScoreMin,
      csatScoreMax: applied?.csatScoreMax,
    },
    pagination,
  );
}

export function formatRelativeHelpdeskTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${Math.max(0, sec)}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

/**
 * How long the customer has been waiting on the agent (`awaitingAgentSince`),
 * or null when nobody is waiting. Purple, then orange after 10 min, red after 30.
 */
export function formatAwaitingReply(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const min = Math.max(0, Math.floor((now - t) / 60000));
  let label = `${min} min`;
  if (min >= 1440) label = `${Math.floor(min / 1440)} d`;
  else if (min >= 60) label = `${Math.floor(min / 60)} h`;
  let color = 'purple';
  if (min >= 30) color = 'red';
  else if (min >= 10) color = 'orange';
  return {minutes: min, label, color};
}

export function stripAiThinking(text) {
  if (typeof text !== 'string') return '';
  return text.replace(/^<think>[\s\S]*?<\/think>\s*/gi, '').trim();
}

export function parseEventPayload(payload) {
  if (payload == null) return {};
  if (typeof payload === 'object') return payload;
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return {text: payload}; // fallback if it's just a string
    }
  }
  return {};
}

const USER_LANGUAGE_LABELS = {
  EN: 'English',
  FR: 'French',
};

export function formatUserLanguageDisplay(code) {
  if (code == null || String(code).trim() === '') {
    return '—';
  }
  const key = String(code).trim().toUpperCase();
  return USER_LANGUAGE_LABELS[key] || key;
}

export function formatAgentDisplayName(agent, fallback) {
  if (!agent) return fallback ?? null;
  const first = agent.firstName?.trim?.() || '';
  const last = agent.lastName?.trim?.() || '';
  const full = `${first} ${last}`.trim();
  return full || agent.email || fallback || null;
}

// First name only — used for admin/agent labels inside the single-ticket
// conversation (message sender + "assigned to" system lines). Falls back to the
// first token of a stored full-name string, then email.
export function formatAgentFirstName(agent, fallback) {
  const first = agent?.firstName?.trim?.();
  if (first) return first;
  const fb = (fallback || '').trim();
  if (fb) return fb.split(/\s+/)[0];
  return agent?.email || null;
}

// Cap a customer's display name to at most the last two words (a hyphenated
// name like "Marie-Laure" has no whitespace, so it counts as one). Used in the
// ticket list views (card / table / chat) so a long full name renders compactly
// — e.g. "Oro Marie Laure" → "Marie Laure". Emails/anonymous fallbacks have no
// spaces, so they pass through untouched.
export function capCustomerName(name, max = 2) {
  const s = (name || '').trim();
  if (!s) return s;
  const parts = s.split(/\s+/);
  if (parts.length <= max) return s;
  return parts.slice(-max).join(' ');
}

const EVENT_SENDER_LABEL = {
  USER_MESSAGE: 'Customer',
  AI_MESSAGE: 'Sassandra',
  AGENT_MESSAGE: 'Agent',
  SYSTEM_EVENT: 'System',
  INTERNAL_NOTE: 'Internal note',
  FLOW_STEP: 'Flow',
};

/** Short, human-readable preview of an event's text for cards/tables. */
export function getEventPreview(event) {
  if (!event) return {text: '', sender: null, eventType: null};
  const payload = parseEventPayload(event.payload);
  const eventType = event.eventType;
  let text = '';
  switch (eventType) {
    case 'AI_MESSAGE':
      text = stripAiThinking(payload.text);
      break;
    case 'USER_MESSAGE':
    case 'AGENT_MESSAGE':
    case 'SYSTEM_EVENT':
    case 'INTERNAL_NOTE':
      text = payload.text || '';
      break;
    case 'FLOW_STEP':
      text = payload.label ? `Selected: ${payload.label}` : '';
      break;
    default:
      text = payload.text || '';
  }
  if (!text && Array.isArray(payload.files) && payload.files.length) {
    text = `📎 ${payload.files.length} attachment${
      payload.files.length > 1 ? 's' : ''
    }`;
  }
  return {
    text: (text || '').replace(/\s+/g, ' ').trim(),
    sender: EVENT_SENDER_LABEL[eventType] || null,
    eventType,
  };
}

export function mapHelpdeskTicketToView(ticket, adminId) {
  const events = ticket?.events || [];
  // events are fetched limited to 1 + sorted DESC → events[0] is the latest;
  // fall back to the max-createdAt entry if a full list ever comes through.
  const latestEvent =
    events.length <= 1
      ? events[0] || null
      : [...events].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )[0];
  const last = latestEvent;
  const payload = parseEventPayload(last?.payload);
  const lastMessage = getEventPreview(latestEvent);
  const uid = ticket?.userId != null ? String(ticket.userId) : '';
  const firstName = ticket?.user?.firstName?.trim?.() || '';
  const lastName = ticket?.user?.lastName?.trim?.() || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const email = ticket?.user?.email || '';
  const contactEmail = ticket?.contactEmail || '';
  const isAnonymous = ticket?.origin === 'ANONYMOUS';
  // Anonymous + unlinked: no user record yet, show the unverified contactEmail
  // the customer typed when they opened the ticket.
  const fallbackName = uid ? `…${uid.slice(-6)}` : contactEmail || 'Anonymous';
  // Cap the customer name to the last two words for the compact list views.
  const displayName = capCustomerName(fullName || email || fallbackName);

  const rawCountry =
    ticket?.user?.countryISO != null
      ? String(ticket.user.countryISO).trim()
      : '';
  const countryISO = rawCountry ? rawCountry.toUpperCase() : null;

  return {
    id: ticket.id,
    userId: uid,
    isAnonymous,
    origin: ticket?.origin || null,
    contactEmail: contactEmail || null,
    linkedAt: ticket?.linkedAt || null,
    linkedByAgentId: ticket?.linkedByAgentId || null,
    userDisplayName: displayName,
    userSecondaryText: fullName
      ? email || contactEmail || fallbackName
      : contactEmail || fallbackName,
    userFirstName: displayName,
    userCountry: countryISO || '—',
    userLanguage: formatUserLanguageDisplay(ticket?.user?.language),
    userInitials:
      `${firstName.charAt(0)}${lastName.charAt(0)}`.trim() ||
      displayName.charAt(0).toUpperCase() ||
      'C',
    userImageUrl: ticket?.user?.imageUrl || null,
    intentCategory:
      ticket.intent || ticket.featureIssue?.title || payload?.label || '—',
    lastMessage: lastMessage.text || '',
    lastMessageSender: lastMessage.sender || null,
    lastMessageType: lastMessage.eventType || null,
    status: ticket.status,
    sentiment: 'neutral',
    modelUsed: '—',
    timeElapsed: formatRelativeHelpdeskTime(
      ticket.updatedAt || ticket.createdAt,
    ),
    csatScore: typeof ticket.csatScore === 'number' ? ticket.csatScore : null,
    resolvedAt: ticket.resolvedAt || null,
    priority: (ticket.escalation?.priority || 'low').toLowerCase(),
    escalationReason: ticket.escalation?.reason || null,
    escalationTeam: ticket.escalation?.team || null,
    agentAssigned:
      ticket.assignedAgentId &&
      adminId &&
      String(ticket.assignedAgentId) === String(adminId)
        ? 'Current Agent'
        : formatAgentDisplayName(
            ticket.assignedAgent,
            ticket.assignedAgentName,
          ),
    resolutionType: ticket.resolutionType || null,
    dateEscalated: ticket.escalation?.escalatedAt || null,
    dateTakenOver: ticket.assignedAt || null,
    awaitingReply: formatAwaitingReply(ticket.awaitingAgentSince),
    _raw: ticket,
  };
}

export function filterTicketsForTab(rows, activeTab, filters = {}) {
  // The server now scopes each tab (and any sidebar status selection) to the
  // right statuses, so this is just a defensive net.
  const selected = [
    ...(Array.isArray(filters?.statuses) ? filters.statuses : []),
    ...(filters?.status ? [filters.status] : []),
  ].filter(Boolean);

  // The Anonymous tab is now scoped to the ANONYMOUS origin server-side via the
  // `origins` filter, so this is just a defensive net (mirrors the status one).
  let scoped = rows;
  if (activeTab === 'anonymous-tickets') {
    scoped = rows.filter(t => {
      const origin = t?.origin || t?._raw?.origin;
      return origin === 'ANONYMOUS';
    });
  }

  // Keep each tab inside its own status set (e.g. abandoned tickets belong to
  // the Abandoned tab, never My Tickets). `null` = no status constraint.
  const tabStatuses = TAB_ALLOWED_STATUSES[activeTab] || null;
  if (tabStatuses) {
    const allowedByTab = new Set(tabStatuses);
    scoped = scoped.filter(t => allowedByTab.has(t.status));
  }

  if (selected.length) {
    const allowed = new Set(selected);
    return scoped.filter(t => allowed.has(t.status));
  }
  return scoped;
}
